import * as React from 'react';
import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { UserSummary } from '../../types/global/helper';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { createFullName } from '../../utils/AppUtils';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { isEmpty } from '../../utils/lodash';
import { avatar_female, avatar_male } from '../../asset/images';
import CustomLazyLoadImage from '../Image/CustomLazyLoadImage';

export interface IUserCardProps {
    user: UserSummary;
    onClickUserCard?: (user: UserSummary) => void;
}

const formatRoleLabel = (raw: string) => raw.replace(/_/g, ' ').trim().toUpperCase();

export const UserCard: React.FC<IUserCardProps> = ({ user, onClickUserCard }) => {
    const { themeStyle } = useThemeContext();
    const selectedAvatar = user.gender === 'female' ? avatar_female : avatar_male;
    const primaryRole = user.user_type && !isEmpty(user.user_type) ? user.user_type[0] : '';
    const extraRoleCount =
        user.user_type && !isEmpty(user.user_type) && user.user_type.length > 1 ? user.user_type.length - 1 : 0;
    const accent = theme[themeStyle].true_highlight;
    const surfaceLow = themeStyle === 'dark' ? '#131313' : theme[themeStyle].deep_grey;
    const outlineVariant = alpha(theme[themeStyle].tag_white, 0.08);

    const handleCardClick = () => {
        onClickUserCard && onClickUserCard(user);
    };
    return (
        <Box
            onClick={handleCardClick}
            role="button"
            tabIndex={0}
            onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleCardClick();
                }
            }}
            sx={{
                position: 'relative',
                width: '100%',
                maxWidth: '100%',
                cursor: 'pointer',
                borderRadius: 2,
                outline: 'none',
                transition: 'transform 0.45s ease',
                '&:hover': {
                    transform: 'scale(1.02)',
                },
                '&:focus-visible': {
                    boxShadow: `0 0 0 2px ${accent}`,
                },
                '&:hover .user-card-glow': {
                    backgroundColor: alpha(accent, 0.12),
                },
            }}>
            {/* Soft glow (cinematic mood) */}
            <Box
                className="user-card-glow"
                aria-hidden
                sx={{
                    position: 'absolute',
                    inset: -16,
                    borderRadius: '50%',
                    zIndex: 0,
                    pointerEvents: 'none',
                    backgroundColor: alpha(accent, 0.06),
                    filter: 'blur(28px)',
                    transition: 'background-color 0.7s ease',
                }}
            />

            <Box
                sx={{
                    position: 'relative',
                    zIndex: 1,
                    borderRadius: 2,
                    overflow: 'hidden',
                    backgroundColor: surfaceLow,
                    boxShadow: '0px 24px 48px rgba(0, 0, 0, 0.7)',
                }}>
                <Box
                    sx={{
                        position: 'relative',
                        width: '100%',
                        aspectRatio: '3 / 4',
                        overflow: 'hidden',
                    }}>
                    <CustomLazyLoadImage
                        effect="blur"
                        src={user.profile_picture ?? selectedAvatar}
                        alt={`${user.firstName}_image`}
                        showGradient={false}
                        width="100%"
                        height="100%"
                        style={{
                            position: 'absolute',
                            inset: 0,
                            objectFit: 'cover',
                            width: '100%',
                            height: '100%',
                            filter: 'brightness(0.85) contrast(1.1)',
                        }}
                    />
                    {/* Gradient scrim */}
                    <Box
                        aria-hidden
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            background: `linear-gradient(to top, ${theme[themeStyle].pure_black}ee 0%, transparent 55%)`,
                            opacity: 0.92,
                            pointerEvents: 'none',
                        }}
                    />

                    {/* Editorial overlay */}
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            p: { xs: 2.5, sm: 3 },
                            pt: { xs: 6, sm: 7 },
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                        }}>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography
                                component="h3"
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: { xs: '1.35rem', sm: '1.65rem' },
                                    fontWeight: 800,
                                    letterSpacing: '-0.03em',
                                    lineHeight: 1.1,
                                    color: theme[themeStyle].tag_white,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    mb: 0.5,
                                }}>
                                {createFullName(user)}
                            </Typography>
                            <Typography
                                component="p"
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: { xs: '1.1rem', sm: '1.25rem' },
                                    fontWeight: 900,
                                    letterSpacing: '-0.04em',
                                    lineHeight: 1,
                                    color: accent,
                                    textShadow: '0 0 15px rgba(255, 159, 77, 0.28)',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}>
                                @{user.userName}
                            </Typography>
                        </Box>

                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 1.5,
                                minWidth: 0,
                            }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, flex: 1 }}>
                                {primaryRole ? (
                                    <Box
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            py: 0.5,
                                            px: 1.25,
                                            borderRadius: 999,
                                            border: `1px solid ${alpha(accent, 0.22)}`,
                                            backgroundColor: alpha(accent, 0.06),
                                            minWidth: 0,
                                        }}>
                                        <Typography
                                            sx={{
                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                fontSize: 10,
                                                fontWeight: 800,
                                                letterSpacing: '0.15em',
                                                textTransform: 'uppercase',
                                                color: accent,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}>
                                            {formatRoleLabel(primaryRole)}
                                        </Typography>
                                    </Box>
                                ) : null}
                                {extraRoleCount > 0 ? (
                                    <Box
                                        sx={{
                                            flexShrink: 0,
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            height: 24,
                                            px: 0.75,
                                            borderRadius: 999,
                                            border: `1px solid ${outlineVariant}`,
                                            backgroundColor: 'rgba(38, 38, 38, 0.55)',
                                        }}>
                                        <Typography
                                            sx={{
                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                fontSize: 10,
                                                fontWeight: 800,
                                                color: theme[themeStyle].grey,
                                            }}>
                                            +{extraRoleCount}
                                        </Typography>
                                    </Box>
                                ) : null}
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};
